/**
 * =============================================================================
 *  FIX: Cart Drawer — Dynamic AJAX + Close/Toggle Fix + Remove Button
 * =============================================================================
 *
 * PROBLEMS FIXED:
 *   1. Close button (x) in cart drawer not working
 *      → Changed document.querySelector('.cart-toggle') to querySelectorAll
 *        so ALL toggle buttons get the click listener.
 *
 *   2. Cart drawer not updating after add/remove
 *      → Intercept add-to-cart form, submit via AJAX to /cart/add.js,
 *        fetch /cart.js, redraw drawer HTML with updated items/prices.
 *
 *   3. Subtotal not updating when quantity changes
 *      → After updating via /cart/update.js, re-render the footer total
 *        with the fresh total_price from the API response.
 *
 *   4. Remove button not present
 *      → Added ✕ button inside each cart item, calls /cart/update.js
 *        with quantity 0 to remove the item.
 *
 *   5. Quantity hitting 0 should remove item
 *      → When qty reaches 0, calls removeCartItem(key) which sends
 *        quantity: 0 to the API, removing the line.
 *
 * =============================================================================
 *  HOW TO APPLY
 * =============================================================================
 *
 *   In Shopify Admin:
 *     1. Online Store → Themes → Actions → Edit Code
 *     2. Open assets/main.js
 *     3. DELETE the entire contents of the file
 *     4. PASTE the entire code below
 *     5. Click Save
 *     6. Refresh your storefront and test
 *
 * =============================================================================
 *  FILES AFFECTED
 * =============================================================================
 *
 *   Only assets/main.js — no changes to theme.liquid, style.css, or any
 *   other file.
 *
 * =============================================================================
 */

document.addEventListener('DOMContentLoaded', () => {

  // ===== HEADER SCROLL EFFECT =====
  const header = document.querySelector('.site-header');
  const handleScroll = () => {
    header.classList.toggle('scrolled', window.scrollY > 80);
  };
  if (header) {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
  }

  // ===== MOBILE MENU =====
  const mobileToggle = document.querySelector('.mobile-toggle');
  const mainNav = document.querySelector('.main-nav');
  if (mobileToggle) {
    mobileToggle.addEventListener('click', () => {
      mainNav.classList.toggle('open');
      mobileToggle.classList.toggle('open');
      document.body.classList.toggle('menu-open');
    });
  }

  // ===== SMOOTH SCROLL =====
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', e => {
      const target = document.querySelector(anchor.getAttribute('href'));
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  // ===== FAQ ACCORDION =====
  document.querySelectorAll('.faq-item').forEach(item => {
    const question = item.querySelector('.faq-question');
    if (question) {
      question.addEventListener('click', () => {
        const isActive = item.classList.contains('active');
        item.closest('.faq-list').querySelectorAll('.faq-item').forEach(i => i.classList.remove('active'));
        if (!isActive) item.classList.add('active');
      });
    }
  });

  // ===== QUIZ / MATTRESS FINDER =====
  document.querySelectorAll('.quiz-section').forEach(quizSection => {
    const steps = quizSection.querySelectorAll('.quiz-step');
    const options = quizSection.querySelectorAll('.quiz-option');
    const result = quizSection.querySelector('.quiz-result');
    const retakeBtn = quizSection.querySelector('.retake-quiz');
    let currentStep = 0;

    const showStep = index => steps.forEach((s, i) => s.classList.toggle('active', i === index));

    options.forEach(option => {
      option.addEventListener('click', () => {
        const step = option.closest('.quiz-step');
        step.querySelectorAll('.quiz-option').forEach(o => o.classList.remove('selected'));
        option.classList.add('selected');

        if (currentStep < steps.length - 1) {
          currentStep++;
          showStep(currentStep);
        } else {
          steps.forEach(s => s.classList.remove('active'));
          if (result) result.classList.add('active');
        }
      });
    });

    if (retakeBtn) {
      retakeBtn.addEventListener('click', () => {
        currentStep = 0;
        options.forEach(o => o.classList.remove('selected'));
        if (result) result.classList.remove('active');
        showStep(0);
      });
    }

    showStep(0);
  });

  // ===== PRODUCT GALLERY THUMBNAILS =====
  const mainGallery = document.querySelector('.product-gallery-main');
  if (mainGallery) {
    const mainImg = mainGallery.querySelector('img');
    document.querySelectorAll('.product-thumb').forEach(thumb => {
      thumb.addEventListener('click', () => {
        const newSrc = thumb.querySelector('img')?.getAttribute('src');
        if (newSrc) {
          mainImg.setAttribute('src', newSrc);
          document.querySelectorAll('.product-thumb').forEach(t => t.classList.remove('active'));
          thumb.classList.add('active');
        }
      });
    });
  }

  // ===== SIZE SELECTOR =====
  document.querySelectorAll('.size-option').forEach(opt => {
    opt.addEventListener('click', () => {
      const parent = opt.closest('.size-options');
      if (parent) {
        parent.querySelectorAll('.size-option').forEach(o => o.classList.remove('selected'));
        opt.classList.add('selected');
      }
    });
  });
  const firstGroup = document.querySelector('.size-options');
  if (firstGroup) {
    const first = firstGroup.querySelector('.size-option');
    if (first) first.classList.add('selected');
  }

  // ===== QUANTITY SELECTOR (product page, not drawer) =====
  document.querySelectorAll('.qty-selector').forEach(selector => {
    const btnMinus = selector.querySelector('.qty-btn:first-child');
    const btnPlus = selector.querySelector('.qty-btn:last-child');
    const input = selector.querySelector('.qty-input');

    if (!btnMinus || !btnPlus || !input) return;

    btnMinus.addEventListener('click', () => {
      let val = parseInt(input.value) || 1;
      if (val > 1) input.value = val - 1;
    });

    btnPlus.addEventListener('click', () => {
      let val = parseInt(input.value) || 1;
      input.value = val + 1;
    });

    input.addEventListener('change', () => {
      let val = parseInt(input.value) || 1;
      if (val < 1) val = 1;
      input.value = val;
    });
  });

  // ===== SCROLL REVEAL ANIMATIONS =====
  const revealObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('revealed');
        revealObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
  document.querySelectorAll('.reveal').forEach(el => revealObserver.observe(el));

  // ===== FILTER BUTTONS =====
  const filterBtns = document.querySelectorAll('.filter-btn');
  const filterProducts = document.querySelectorAll('.product-card');
  if (filterBtns.length) {
    filterBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        filterBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const filter = btn.dataset.filter || 'all';
        filterProducts.forEach(product => {
          product.style.display = (filter === 'all' || (product.dataset.tags || '').includes(filter)) ? '' : 'none';
        });
      });
    });
  }

  // ===== ANNOUNCEMENT BAR CLOSE =====
  const closeAnnouncement = document.querySelector('.announcement-close');
  if (closeAnnouncement) {
    closeAnnouncement.addEventListener('click', () => {
      const bar = closeAnnouncement.closest('.announcement-bar');
      if (bar) bar.style.display = 'none';
    });
  }

  // ===================================================================
  //  CART DRAWER — FIXED TOGGLE + DYNAMIC AJAX + REMOVE BUTTON
  // ===================================================================

  // --- Toggle: works on ALL .cart-toggle buttons (incl. close X) ---
  document.querySelectorAll('.cart-toggle').forEach(toggle => {
    toggle.addEventListener('click', e => {
      e.preventDefault();
      const drawer = document.querySelector('.cart-drawer');
      if (!drawer) return;
      const wasClosed = !drawer.classList.contains('open');
      drawer.classList.toggle('open');
      document.body.classList.toggle('drawer-open');
      if (wasClosed) fetchAndRenderCart();
    });
  });

  // --- Close on overlay click ---
  document.querySelector('.drawer-overlay')?.addEventListener('click', () => {
    document.querySelector('.cart-drawer')?.classList.remove('open');
    document.body.classList.remove('drawer-open');
  });

  // --- Format cents to ₹ (reliable, no locale issues) ---
  window.cartHelpers = {
    formatMoney: function (cents) {
      const num = (cents / 100).toFixed(2);
      return '\u20B9' + num;
    },
    escapeHtml: function (str) {
      if (!str) return '';
      const d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    }
  };

  // --- Fetch cart and re-render ---
  function fetchAndRenderCart() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => renderCartDrawer(cart))
      .catch(e => console.error('Cart fetch error:', e));
  }

  // --- Render cart drawer from API data ---
  function renderCartDrawer(cart) {
    const body = document.querySelector('.cart-drawer-body');
    const footer = document.querySelector('.cart-drawer-footer');
    if (!body) return;

    // Update badge
    const count = document.querySelector('.cart-count');
    if (count) count.textContent = cart.item_count;

    if (cart.item_count === 0) {
      body.innerHTML = '<p class="cart-drawer-empty">Your cart is empty</p>';
      if (footer) footer.style.display = 'none';
      return;
    }
    if (footer) footer.style.display = '';

    const fm = window.cartHelpers;

    let html = '';
    cart.items.forEach(item => {
      const img = item.image ? item.image.replace(/(\?.*)?$/, '?width=100') : '';
      const title = fm.escapeHtml(item.product_title);
      const variant = (item.variant_title && item.variant_title !== 'Default Title')
        ? '<p>' + fm.escapeHtml(item.variant_title) + '</p>' : '';

      html += '<div class="cart-drawer-item" data-key="' + item.key + '">'
        + '<img src="' + img + '" alt="' + title + '" loading="lazy">'
        + '<div class="cart-drawer-item-details">'
        + '<div style="display:flex;justify-content:space-between;align-items:flex-start;">'
        + '<h4>' + title + '</h4>'
        + '<button class="cart-item-remove" data-key="' + item.key + '" aria-label="Remove item" style="background:none;border:none;cursor:pointer;padding:2px 6px;color:#999;font-size:16px;line-height:1;border-radius:4px;transition:all .15s;" onmouseover="this.style.color=\'#e63946\';this.style.background=\'#fff0f0\'" onmouseout="this.style.color=\'#999\';this.style.background=\'none\'">✕</button>'
        + '</div>'
        + variant
        + '<p>' + fm.formatMoney(item.line_price) + '</p>'
        + '<div class="qty-selector" style="height:32px;">'
        + '<button class="qty-btn cart-qty-btn" data-key="' + item.key + '" data-change="-1" style="width:32px;height:32px;font-size:var(--fs-sm);">−</button>'
        + '<input type="number" class="qty-input" value="' + item.quantity + '" min="0" readonly style="width:40px;height:32px;font-size:var(--fs-sm);">'
        + '<button class="qty-btn cart-qty-btn" data-key="' + item.key + '" data-change="1" style="width:32px;height:32px;font-size:var(--fs-sm);">+</button>'
        + '</div>'
        + '</div>'
        + '</div>';
    });
    body.innerHTML = html;

    // Update subtotal in footer
    const totalEl = document.querySelector('.cart-drawer-total');
    if (totalEl) {
      totalEl.innerHTML = '<span><strong>Subtotal:</strong></span><span>' + fm.formatMoney(cart.total_price) + '</span>';
    }

    // Attach qty +/- handlers
    body.querySelectorAll('.cart-qty-btn').forEach(btn => {
      btn.addEventListener('click', function () {
        const key = this.dataset.key;
        const change = parseInt(this.dataset.change);
        const input = this.closest('.qty-selector').querySelector('.qty-input');
        let qty = parseInt(input.value) + change;
        if (qty <= 0) {
          removeCartItem(key);
        } else {
          updateCartItem(key, qty);
        }
      });
    });

    // Attach remove button handlers
    body.querySelectorAll('.cart-item-remove').forEach(btn => {
      btn.addEventListener('click', function () {
        removeCartItem(this.dataset.key);
      });
    });
  }

  // --- Update item quantity via AJAX ---
  function updateCartItem(key, qty) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: { [key]: qty } })
    })
      .then(r => {
        if (!r.ok) throw new Error('Update failed');
        return r.json();
      })
      .then(cart => {
        renderCartDrawer(cart);
        const count = document.querySelector('.cart-count');
        if (count) count.textContent = cart.item_count;
      })
      .catch(e => console.error('Cart update error:', e));
  }

  // --- Remove item (qty = 0) ---
  function removeCartItem(key) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: { [key]: 0 } })
    })
      .then(r => {
        if (!r.ok) throw new Error('Remove failed');
        return r.json();
      })
      .then(cart => {
        renderCartDrawer(cart);
        const count = document.querySelector('.cart-count');
        if (count) count.textContent = cart.item_count;
      })
      .catch(e => console.error('Cart remove error:', e));
  }

  // --- AJAX Add to Cart ---
  document.addEventListener('submit', function (e) {
    const form = e.target.closest('form[action="/cart/add"]');
    if (!form) return;
    e.preventDefault();

    const formData = new FormData(form);
    const id = formData.get('id');
    const qty = formData.get('quantity') || 1;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: parseInt(id), quantity: parseInt(qty) })
    })
      .then(r => {
        if (!r.ok) throw new Error('Add to cart failed');
        return r.json();
      })
      .then(() => {
        fetchAndRenderCart();
        document.querySelector('.cart-drawer')?.classList.add('open');
        document.body.classList.add('drawer-open');
      })
      .catch(e => console.error('Add to cart error:', e));
  });

  // ===================================================================
  //  END CART DRAWER FIXES
  // ===================================================================

  // ===== COUNTER ANIMATION =====
  const counters = document.querySelectorAll('.stat-number .count');
  if (counters.length) {
    const counterObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const target = parseInt(el.dataset.target);
          const duration = 2000;
          const start = performance.now();

          const animate = time => {
            const elapsed = time - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = Math.floor(eased * target);
            el.textContent = current.toLocaleString();
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              el.textContent = target.toLocaleString();
            }
          };

          requestAnimationFrame(animate);
          counterObserver.unobserve(el);
        }
      });
    }, { threshold: 0.5 });
    counters.forEach(c => counterObserver.observe(c));
  }
});
